import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { tagsDb } from '../../lib/db/index.js';
import { useAuth } from '../../lib/firebase/auth-context.js';
import { TAG_TYPES } from '../../types/tag.js';
import {
  filterTagsBySearch,
  groupTagsByType,
  splitMasterAndOrphan,
} from '../../lib/utils/tag-input-logic.js';
import { Spinner } from '../shared/Spinner.js';

interface Props {
  value: string[];
  onChange: (next: string[]) => void;
  max?: number;
}

/**
 * 投稿時のタグ選択 UI（厳格モード）。
 *
 * - tags マスタ（コレクション）から取得した選択肢を type 別グルーピングで提示
 * - 自由入力は不可。新規タグは管理者が `TagsTab` で追加してから利用
 * - 編集モードで既存の「マスタ外タグ」がある場合は別セクションで表示し、
 *   × で外せるが新規追加はできない（過去データの保持）
 *
 * 保存形式は従来どおり `string[]`（タグ名）。`tags/{id}` の id ではなく name を保存する設計
 * を維持しているため、Rules でマスタ存在性は検証していない（UI 層で厳格化）。
 */
export function TagInput({ value, onChange, max = 10 }: Props) {
  const { fbUser } = useAuth();
  const [search, setSearch] = useState('');
  // 選択肢パネルは折りたたみ式。デフォルトで閉じておき、編集時のみ開く（縦領域節約）
  const [pickerOpen, setPickerOpen] = useState(false);

  const tagsQ = useQuery({
    queryKey: ['tags'],
    queryFn: () => tagsDb.findAll(),
    staleTime: 5 * 60_000,
    // 未認証時の Rules 拒否例外を防ぐ。Compose は RequireAuth 配下から開く前提だが
    // ログアウト → モーダル残存のレース対策として明示
    enabled: !!fbUser,
  });

  const masterAll = tagsQ.data ?? [];
  const filtered = useMemo(
    () => filterTagsBySearch(masterAll, search),
    [masterAll, search],
  );
  const grouped = useMemo(() => groupTagsByType(filtered), [filtered]);
  const { master: selectedMaster, orphan: selectedOrphan } = useMemo(
    () => splitMasterAndOrphan(value, masterAll),
    [value, masterAll],
  );

  const isReached = value.length >= max;

  const toggle = (name: string) => {
    if (value.includes(name)) {
      onChange(value.filter((t) => t !== name));
      return;
    }
    if (isReached) return;
    onChange([...value, name]);
  };

  const removeOrphan = (name: string) => {
    onChange(value.filter((t) => t !== name));
  };

  if (tagsQ.isPending) {
    return (
      <div className="tag-input-master">
        <Spinner />
      </div>
    );
  }
  if (tagsQ.isError) {
    return (
      <div className="tag-input-master">
        <div className="auth-error">
          タグマスタの取得に失敗しました。再読み込みしてください。
        </div>
      </div>
    );
  }

  return (
    <div className="tag-input-master">
      {/* 選択済みチップ + 残数 + 開閉トグル（常時表示） */}
      <div className="tag-input-summary">
        <div className="tag-chips" style={{ flex: 1, minWidth: 0 }}>
          {selectedMaster.map((t) => (
            <span key={`m-${t.id}`} className="tag-chip" data-type={t.type}>
              <span>{t.name}</span>
              <button
                type="button"
                className="tag-chip-x"
                aria-label={`タグ ${t.name} を外す`}
                onClick={() => toggle(t.name)}
              >
                ×
              </button>
            </span>
          ))}
          {value.length === 0 && (
            <span className="tag-input-empty mono">未選択</span>
          )}
        </div>
        <button
          type="button"
          className="btn ghost xs tag-master-toggle"
          onClick={() => {
            setPickerOpen((v) => {
              const next = !v;
              // 閉じる時は検索クエリをリセット（次回開く時に絞り込み残留しないように）
              if (!next) setSearch('');
              return next;
            });
          }}
          aria-expanded={pickerOpen}
          aria-controls="tag-master-picker"
        >
          {pickerOpen ? '▲ 閉じる' : `▼ タグを選ぶ（${value.length}/${max}）`}
        </button>
      </div>

      {/* マスタ外タグ（編集モードで残存している過去データ） */}
      {selectedOrphan.length > 0 && (
        <div className="tag-master-orphan-section">
          <div className="tag-master-orphan-label mono">
            マスタ外（再選択不可、× で外せます）
          </div>
          <div className="tag-chips">
            {selectedOrphan.map((name) => (
              <span key={`o-${name}`} className="tag-chip is-orphan">
                <span>{name}</span>
                <button
                  type="button"
                  className="tag-chip-x"
                  aria-label={`タグ ${name} を外す`}
                  onClick={() => removeOrphan(name)}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* 折りたたみ可能な選択肢パネル */}
      {pickerOpen && (
        <div id="tag-master-picker" className="tag-master-picker">
          {/* autoFocus はモバイルで意図せずキーボード起動するため付けない */}
          <input
            type="text"
            className="tag-master-search"
            placeholder="タグを絞り込み（部分一致）"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <div className="tag-master-groups">
            {TAG_TYPES.map((type) => {
              const list = grouped[type];
              if (list.length === 0) return null;
              return (
                <details key={type} className="tag-master-group" open>
                  <summary className="tag-master-group-header">
                    <span>{type}</span>
                    <span className="mono">{list.length}</span>
                  </summary>
                  <div className="tag-master-options">
                    {list.map((t) => {
                      const selected = value.includes(t.name);
                      const disabled = !selected && isReached;
                      return (
                        <button
                          key={t.id}
                          type="button"
                          className={
                            'tag-master-option' + (selected ? ' is-selected' : '')
                          }
                          data-type={t.type}
                          onClick={() => toggle(t.name)}
                          disabled={disabled}
                          aria-pressed={selected}
                        >
                          {t.name}
                        </button>
                      );
                    })}
                  </div>
                </details>
              );
            })}
          </div>

          <div className="tag-input-help mono">
            {value.length}/{max}
            {isReached && ' · 上限到達（外してから他を選択）'}
          </div>
        </div>
      )}
    </div>
  );
}
